<template>
  <Teleport to="body">
    <Transition name="ko-fade">
      <div v-if="visible" class="ko-overlay lobby-figma">
        <div class="ko-panel">
          <div class="ko-icon" v-html="ICON.warn"></div>
          <div class="ko-title">{{ t('kicked_out_title') }}</div>
          <p class="ko-text">{{ message || t('kicked_out_message') }}</p>
          <F1Button size="lg" block @click="confirmKickedOut">
            {{ t('kicked_out_confirm') }}
          </F1Button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
/**
 * 被踢下线提示弹窗 (账号在其他设备登录)
 *
 * 全局挂载于 App.vue, 由 useKickedOut 单例驱动:
 * 底层 client.ts 调 showKickedOut() → visible 翻转 → 显示 → 用户点确认 → 跳登录。
 *
 * 视觉走大厅 lobby-figma 体系 (暗底 + F1Button 主按钮), 不用 Element el-dialog。
 * 强制操作: 不可点遮罩/ESC 关闭, 只能点"重新登录"。
 */
import { useI18n } from 'vue-i18n'
import { ICON } from '@/assets/lobby-figma/icons'
import F1Button from '@/components/F1Button.vue'
import { useKickedOut, confirmKickedOut } from '@/composables/useKickedOut'

const { t } = useI18n()
const { visible, message } = useKickedOut()
</script>

<style scoped lang="scss">
.ko-overlay {
  position: fixed;
  inset: 0;
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.72);
  // 借用 .lobby-figma 只是为了拿它的 CSS 变量, 但该 class 自带 max-width:430px;margin:0 auto
  // (给整页容器用的规则), 会把这个本该铺满全屏的遮罩也收窄成一条居中窄栏, 这里显式覆盖掉
  max-width: none;
  margin: 0;
}

// 借用 .lobby-figma 的 CSS 变量, 但屏蔽其满屏 ::before(斜纹)/::after(深色背景)伪元素
.ko-overlay::before,
.ko-overlay::after {
  display: none !important;
  content: none !important;
}

.ko-panel {
  width: 100%;
  max-width: 320px;
  padding: 28px 24px 24px;
  border-radius: 16px;
  text-align: center;
  background: var(--surface, #15151e);
  border: 1px solid var(--line, rgba(255, 255, 255, 0.1));
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.5);
}

.ko-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  margin-bottom: 14px;
  border-radius: 50%;
  color: var(--accent, #e8592a);
  background: rgba(232, 89, 42, 0.12);

  :deep(svg) {
    width: 26px;
    height: 26px;
  }
}

.ko-title {
  font-family: var(--font-display);
  font-size: 18px;
  font-weight: 800;
  color: #fff;
  margin-bottom: 10px;
}

.ko-text {
  margin: 0 0 22px;
  font-size: 13px;
  line-height: 1.6;
  color: var(--muted, #9a9aa5);
}

// 过渡
.ko-fade-enter-active,
.ko-fade-leave-active {
  transition: opacity 0.2s ease;
}
.ko-fade-enter-from,
.ko-fade-leave-to {
  opacity: 0;
}
</style>
